<template>
    <div class="modal" id="muven-audit-instance-quick-visualization-modal">
        <div class="modal-dialog modal-xl">
            <div class="modal-content">
                <div class="modal-header">
                    <h5>Auditoria</h5>
                    <p class="mb-0 text-muted">{{ auditInfo.getDescription() }}</p>
                </div>

                <div class="modal-body">
                    <NothingFound v-if="nothingToShow" />

                    <div v-if="!nothingToShow">
                        <div class="table-responsive">
                            <table class="table table-hover table-sm table-striped mb-0">
                                <thead>
                                    <tr>
                                        <th>Descrição</th>
                                        <th class="interaction-col">Interação</th>
                                        <th class="datetime-col">Data e hora</th>
                                        <th class="status-col">Status</th>
                                        <th></th>
                                    </tr>
                                </thead>

                                <tbody v-for="(audit, index) in auditList" :key="index">
                                    <tr>
                                        <td>{{ audit.description }}</td>
                                        <td><AuditActorCmp :audit="audit" :subscriber="subscriber" /></td>
                                        <td class="datetime-col">{{ audit.date | dateTimeFilter }}</td>
                                        <td class="status-col">
                                            <span class="muven-td-icon">
                                                <fa icon="hourglass-start" v-if="audit.isStatusRunning()"
                                                    class="text-muted" />

                                                <fa icon="check-circle" v-if="audit.isStatusCompleted()"
                                                    class="text-success" />

                                                <fa icon="exclamation-triangle" v-if="audit.isStatusError()"
                                                    class="text-danger" />

                                                <fa icon="exclamation-triangle" v-if="audit.isStatusWarning()"
                                                    class="text-warning" />

                                            </span>

                                            <span>{{ audit.getStatusEnumDescription() }}</span>
                                        </td>
                                        <td>
                                            <span v-show="audit.loadingListAction">
                                                <Loading class="text-muted" />
                                            </span>

                                            <span v-show="!audit.loadingListAction">
                                                <fa icon="chevron-down" class="audit-toggle-icon"
                                                    @click="toggleAuditCollapse(audit)"
                                                    :class="{ 'audit-toggle-icon-flipped': !audit.collapsed }" />
                                            </span>
                                        </td>
                                    </tr>

                                    <tr v-if="!audit.collapsed">
                                        <td colspan="5">
                                            <tree-view :deep="4" :highlight-mouseover-node="true" :show-length="true"
                                                :data="auditData.additionalData" />
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <div v-if="showPaginationButton" class="text-center mt-3">
                            <button class="btn btn-outline-secondary" @click="getMoreAuditList()"
                                :disabled="loadingNextPage">
                                <span v-show="!loadingNextPage">Mais resultados</span>
                                <fa v-show="loadingNextPage" icon="sync-alt" spin />
                            </button>
                        </div>
                    </div>
                </div>

                <div class="modal-footer">
                    <button type="button" class="btn btn-outline-secondary" @click="closeModal()">
                        Fechar
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { dateTimeFilter } from '@/utils/filters/DateTimeFilter'
import { AuditInstanceModel } from '@/audit/models/AuditInstanceModel'
import { PaginationModel } from '@/utils/models/PaginationModel'
import auditService from '@/audit/services/AuditService'
import { AuditInstanceListFilterModel } from '@/audit/models/AuditInstanceListFilterModel'
import modalService from '@/utils/services/ModalService'
import GetEntityUrl from '@/utils/mixins/GetEntityUrl'
import ChannelService from '@/channel/services/ChannelService'
import VueJsonPretty from 'vue-json-pretty';
import { mapState } from 'vuex'
import DownloadJson from '../../utils/mixins/DownloadJson'
import { cloneDeep } from 'lodash'
import AuditActorCmp from '@/audit/components/AuditActorCmp.vue'
import { EntityEnum } from '@c2b/muven-commons'

const ID_MODAL = '#muven-audit-instance-quick-visualization-modal';

export default {
    name: 'AuditListQuickVisualizationCmp',
    data() {
        return {
            loading: true,
            loadingNextPage: false,
            showPaginationButton: true,
            auditList: [],
            channelList: [],
            auditInfo: {
                idEntity: 0,
                entityList: EntityEnum.Product,
                getDescription: () => '',
            },
            auditInstanceFilter: new AuditInstanceListFilterModel(),
            auditData: new AuditInstanceModel(),
            pagination: new PaginationModel(),
        }
    },
    components: {
        'tree-view': VueJsonPretty,
        AuditActorCmp,
    },
    filters: {
        dateTimeFilter,
    },
    mixins: [
        GetEntityUrl,
        DownloadJson,
    ],
    methods: {
        downloadAuditHandler(audit) {
            this.downloadJSON(audit, `Auditoria ${audit.getEntityEnumDescription()} - ${audit.date.toISOString()}.json`)
        },
        async getMoreAuditList() {
            try {
                this.loadingNextPage = true
                const newAuditList = await auditService.getInstances(this.pagination.actual(), this.auditInstanceFilter)
                this.auditList.push(...newAuditList)

                this._updateChannelNames()

                if (!newAuditList.length) {
                    this.showPaginationButton = false
                }

                this.pagination.next()
            } catch (e) {
                this.$floatingAlert.error({
                    message: e.message,
                })
            }

            this.loadingNextPage = false
        },
        async showModal(info) {
            this.loading = true
            this.auditInfo = cloneDeep(info)
            this.pagination.reset()

            try {
                this.auditInstanceFilter = new AuditInstanceListFilterModel(this.auditInfo)
                this.channelList = await ChannelService.getChannels()
                this.auditList = await auditService.getInstances(this.pagination.actual(), this.auditInstanceFilter)
                this.pagination.next()
                this._updateChannelNames()

                await modalService.show(ID_MODAL)
            } catch (e) {
                this.$floatingAlert.error({
                    message: e.message,
                })
            }

            this.loading = false
        },
        async closeModal() {
            await modalService.hide(ID_MODAL)
        },
        async toggleAuditCollapse(audit) {
            if (!audit.collapsed) {
                audit.collapsed = true;
                return
            }

            audit.loadingListAction = true

            try {
                this.auditData = await auditService.getInstanceById(audit._id)
            } catch (err) {
                this.$floatingAlert.error({
                    message: err.message,
                })
            }

            audit.loadingListAction = false
            audit.collapsed = !audit.collapsed;
        },
        async _updateChannelNames() {
            this.auditList.forEach((audit) => {
                for (const channel of this.channelList) {
                    if (parseInt(audit.idChannel, 10) === parseInt(channel.id, 10)) {
                        audit.setChannel(channel)
                    }
                }
            })
        },
    },
    computed: {
        ...mapState({
            subscriber: state => state.subscriber,
        }),
        nothingToShow() {
            return !this.loading && !this.auditList.length
        },
    },
}
</script>

<style lang="scss" scoped>
.modal {
    .modal-body {
        max-height: calc(80vh - 100px);
        overflow: auto;

        .table.table-striped {
            tbody {
                &:nth-of-type(2n+2) {
                    tr:nth-of-type(2n+1) {
                        background-color: var(--surface-1);
                    }
                }
            }
        }
    }

    .audit-toggle-icon {
        color: var(--text-muted);
        font-size: 1em;
        transition: transform .3s ease;

        &.audit-toggle-icon-flipped {
            transform: rotate(180deg);
        }
    }
}
</style>